// Schemas
export {
  DancerAvailabilitySchema,
  BulkAvailabilitySchema,
  TeamAvailabilityQuerySchema,
  type DancerAvailabilityData,
  type BulkAvailabilityData,
  type TeamAvailabilityQueryData,
  type AvailabilityType,
} from './schemas';

// TypeScript-first form schemas (Production Standard)
export {
  // Main form schemas
  DancerFormSchema,
  DancerFormUpdateSchema,
  DancerFormSectionSchemas,
  ContractVerificationSchema,

  // Section schemas
  PersonalInfoSchema,
  AddressSchema,
  ProfessionalInfoSchema,
  PhysicalAttributesSchema,
  GeographicAvailabilitySchema,
  EmergencyContactSchema,
  TaxInfoSchema,
  IdentityVerificationSchema,
  PaymentInfoSchema,

  // TypeScript types
  type DancerFormInput,
  type DancerFormUpdateInput,
  type PersonalInfo,
  type AddressInfo,
  type ProfessionalInfo,
  type PhysicalAttributes,
  type GeographicAvailability,
  type EmergencyContact,
  type TaxInfo,
  type IdentityVerification,
  type PaymentInfo,
  type ContractVerification,
} from './schemas/dancer-form.schema';

// NOTE: Server actions are NOT exported from main index to avoid bundling server-only code with client components
// Import server actions directly from './server/actions' in server components or server action imports

// Profile completion utilities
export {
  calculateDancerProfileCompletion,
  getProfileCompletionMessage,
  getSectionLabel,
  getContractDetailsMissingFields,
  type ProfileCompletionField,
  type ProfileCompletionResult,
  type ContractDetailsMissing,
} from './lib/profile-completion';

// Profile mappers for type-safe data transformation
export {
  mapToProfile,
  mapToProfessionalProfile,
  mapToDancerProfile,
  mapJoinedProfileForCompletion,
  mapFullProfileForCompletion,
  type JoinedDancerProfile,
  type DancerProfilesFullView,
} from './lib/profile-mappers';

// Re-export server actions for convenience (server-only)
export { getDancerProfileCompletionAction } from './server/actions';

// NOTE: Profile status utilities are server-only - import from './server/dancer-profile-status' directly

// Components
export {
  AddressInput,
  BankAccountInput,
  HeightImperialInput,
  IdentityVerificationInput,
  MeasurementDisplay,
  MeasurementInput,
  MeasurementInputField,
  PhoneInput,
  SizeSelector,
  UnitToggleButton,
  conversions,
  formatDisplayValue,
  getUnitLabel,
} from './components/form-inputs';
export type {
  AddressData,
  BankAccountData,
  BankAccountInputProps,
  HeightImperialInputProps,
  IdentityVerificationErrors,
  MeasurementDisplayProps,
  MeasurementFieldProps,
  MeasurementInputProps,
  MeasurementType,
  PhoneData,
  PhoneMetadata,
  Unit,
  UnitToggleProps,
} from './components/form-inputs';
export { FormSectionBanner } from './components/form-section-banner';
export { SocialHandleInput } from './components/social-handle-input';
export type { SocialPlatform } from './components/social-handle-input';
export {
  SOCIAL_BRAND_ICONS,
  TikTokIcon,
  VimeoIcon,
  XIcon,
  PinterestIcon,
  SnapchatIcon,
  type SocialBrandIcon,
} from './components/icons/social-brand-icons';
export { MediaCategoryPicker } from './components/media-category-picker';

// Shared display components
export {
  DancerAvatar,
  type DancerAvatarProps,
} from './components/shared/dancer-avatar';

// Public profile components
export { DownloadResumeButton } from './components/public-profile/download-resume-button';

// Document-only verification type
export type { DocumentOnlyVerification } from './components/form-inputs/identity-verification-input';

// NOTE: DancerGuard is server-only - import directly from './components/dancer-guard' in server components only

// Contract setup service
export {
  ContractSetupService,
  type ContractSetupState,
} from './services/contract-setup.service';

// Cryptographic service
export {
  CryptoService,
  cryptoUtils,
  type SignatureResult,
  type SignatureWithMetadata,
} from './services/crypto.service';

// Dancer media service
export {
  CROP_ASPECT,
  CROP_ASPECT_TOLERANCE,
  DancerMediaService,
  validateCropRect,
  type CropRectInput,
  type DancerMedia,
  type FinalizeDirectUploadInput,
  type MediaUploadInput,
  type VideoLinkInput,
} from './services/dancer-media.service';

// Dancer location service
export {
  DancerLocationService,
  type DancerLocation,
  type CreateLocationInput,
  type UpdateLocationInput,
} from './services/dancer-location.service';

// Shared canonical residence-write mapper (Rule 8 — one mapper feeds all
// three current-residence edit surfaces). See BAL-635.
export {
  mapResidenceToCanonicalLocation,
  type ResidenceLocationPayload,
  type MapResidenceOptions,
} from './lib/map-residence-to-canonical-location';

// Server-side canonical geocoder (BAL-635) — the shared free-text-city →
// canonical residence payload path used by the WhatsApp Flow AI tools and the
// one-time backfill (Rule 8: one canonical geocode path).
export { geocodeResidenceCity } from './lib/geocode-residence-city';

// Dancer repertoire service
export {
  createDancerRepertoireService,
  type DancerRepertoireService,
  type Choreographer,
  type Piece,
  type PieceOption,
  type DancerRepertoireEntry,
  type CreateRepertoireInput,
  type UpdateRepertoireInput,
} from './services/dancer-repertoire.service';

// Dancer contract service
export {
  DancerContractService,
  type SaveContractDetailsInput,
} from './services/dancer-contract.service';

// Public Featured Events — past, accepted cast assignments displayed on
// /artists/[slug]. Backed by SECURITY DEFINER RPC `get_public_featured_events`.
export {
  createFeaturedEventsService,
  type FeaturedEventsService,
  type PublicFeaturedEvent,
} from './services/featured-events.service';

// Dancer-self-published performances (galas / tours / workshops / appearances).
// Parallel source to featured events; written by the owning dancer or admin.
export {
  PerformancesService,
  createPerformancesService,
  type Performance,
  type PerformanceType,
  type PerformanceInsert,
  type PerformanceUpdate,
} from './services/performances.service';

// Performance Zod schemas (used by editor forms + server actions).
export {
  performanceInsertSchema,
  performanceUpdateSchema,
  performanceTypeSchema,
  type PerformanceInsertInput,
  type PerformanceInsertOutput,
  type PerformanceUpdateInput,
  type PerformanceUpdateOutput,
  type PerformanceTypeInput,
} from './schemas/performance.schema';

// NOTE: Resume generation now uses API Route pattern - see /api/pdf/resume
// The ResumeExperience type is defined locally in the API route

// Dancer constants (PWA-compatible enums)
export {
  GENDER,
  GENDER_VALUES,
  GENDER_LABELS,
  LOOKING_FOR,
  LOOKING_FOR_VALUES,
  LOOKING_FOR_ALL_VALUES,
  LOOKING_FOR_LABELS,
  DANCE_STYLE,
  DANCE_STYLE_VALUES,
  DANCE_STYLE_LABELS,
  DANCE_LEVEL,
  DANCE_LEVEL_VALUES,
  DANCE_LEVEL_LABELS,
  getGenderLabel,
  getLookingForLabel,
  getLookingForLabels,
  getDanceStyleLabel,
  getDanceStyleLabels,
  getDanceLevelLabel,
  type GenderType,
  type LookingForType,
  type DanceStyleType,
  type DanceLevelType,
} from './constants/dancer.constants';

// Google Maps utilities
export {
  loadGoogleMapsAPI,
  isGoogleMapsLoaded,
  type GoogleMapsLoaderConfig,
} from './lib/google-maps-loader';

// Google Maps React hook (client-only)
export { useGoogleMapsAPI } from './lib/use-google-maps';

// Media editorial categories — vocabulary + bucketing helpers used by
// the public render (`dancer-gallery.tsx`, `dancer-videos.tsx`) and the
// editor surfaces (setup wizard, admin media tab). Backed by the
// `artist_media_category_check` DB CHECK constraint.
export {
  PHOTO_CATEGORIES,
  VIDEO_CATEGORIES,
  PHOTO_CATEGORY_VALUES,
  VIDEO_CATEGORY_VALUES,
  LEGACY_CATEGORY_VALUES,
  DancerMediaCategorySchema,
  DancerMediaCategoryNullableSchema,
  isPhotoCategory,
  isVideoCategory,
  isLegacyCategory,
  getPhotoCategoryLabel,
  getVideoCategoryLabel,
  bucketByCategory,
  type PhotoCategory,
  type VideoCategory,
  type MediaCategory,
  type MediaCategoryOption,
} from './lib/media-categories';

// Country utilities (complete ISO 3166-1 list)
export {
  ALL_COUNTRIES,
  PRIORITY_COUNTRY_CODES,
  getSortedCountries,
  getAllCountryOptions,
  getCountryFieldOptions,
  getCountryByCode,
  getCountryName,
  getFlagEmoji,
  searchCountries,
  // Banking system utilities
  IBAN_COUNTRY_CODES,
  isIbanCountry,
  VAT_COUNTRY_CODES,
  isVatCountry,
  US_BANKING_COUNTRY_CODES,
  isUsBankingCountry,
  UK_BANKING_COUNTRY_CODES,
  isUkBankingCountry,
  type Country,
  type CountryOption,
  // Language utilities (ISO 639-1)
  getAllLanguageOptions,
  getLanguageName,
  type LanguageOption,
} from './lib/countries';

// Age calculation util — Meteor-parity Age display on the public
// profile is computed from `profiles.date_of_birth`. Inlined here so
// callers don't reimplement the leap-year / future-date / sanity-bound
// logic inline.
export { calculateAge } from './lib/age';

// Age-gated "Available for" rules (BAL-248 §11b) + resume eligibility
// (BAL-248 §6a). Display-time helpers consumed by the public dancer view.
export {
  availabilityAgeRules,
  isWorkTypeVisibleAtAge,
  filterLookingForByAge,
  type AvailabilityAgeRule,
} from './lib/availability-age-rules';
export {
  isResumeEligible,
  type ResumeEligibilityInput,
} from './lib/resume-eligibility';

// Community browse discovery level (Professional / Pre-Professional chips).
// SSOT: SQL classifies the base level; this resolves the age branch. See
// `lib/discovery-level.ts`.
export {
  resolveDiscoveryLevel,
  preProfessionalThresholdDate,
  PRE_PROFESSIONAL_AGE_CUTOFF,
  type DiscoveryLevel,
  type DiscoveryLevelBase,
  type ResolveDiscoveryLevelInput,
} from './lib/discovery-level';

// Locale-aware display names — turn ISO codes (US, en) into human
// names (United States, English) in the user's current locale.
// Centralised so render sites never display the raw codes.
export { countryDisplayName, languageDisplayName } from './lib/display-names';

// Viewer-role detection helper — derives `'anonymous' | 'authenticated'
// | 'recruiter' | 'owner' | 'admin'` from the auth session and
// membership tables. Use at the page level to build a `ViewContext`
// for the visibility registry.
export { getViewerRole } from './lib/viewer-role';

// Per-dancer field gates — the one way to withhold an opted-out field on an
// egress path. Keyed by toggle name so the emission line names the decision.
// See `lib/dancer-field-gates.ts`.
export {
  applyDancerFieldGates,
  closedDancerFieldGates,
  dancerFieldGates,
  fetchDancerFieldGates,
  fetchDancerFieldGatesBatch,
  VISIBILITY_COLUMNS,
  type DancerFieldGateOptions,
  type DancerFieldGates,
  type FieldGate,
} from './lib/dancer-field-gates';

// Per-surface view-fields registry — picks which profile fields/media
// each surface (public profile, agency, competition snapshot, etc.)
// renders, scoped by brand and viewer role. Render sites consume the
// helpers; do not hardcode visibility rules. See `lib/view-fields.ts`
// for the full registry + decisions.
export {
  ALWAYS,
  AUTHENTICATED_ONLY,
  anyOf,
  CAST_SIGNAL_FIELDS,
  FEVER_OR_ADMIN,
  FIELD_VISIBILITY,
  GATED_COLUMNS,
  GATED_COLUMNS_BY_TOGGLE,
  GATED_FIELD_KEYS,
  NEVER,
  ONLY_OWNER,
  OWNER_OR_RECRUITER,
  getFieldsForView,
  getFieldsForViewWithContext,
  getMediaForView,
  getMediaForViewWithContext,
  isFieldVisible,
  isFieldVisibleInView,
  isMediaVisible,
  isMediaVisibleInView,
  onlyOnBrand,
  toggleForColumn,
  type Brand,
  type DancerVisibilityPreferences,
  type FieldRule,
  type FieldVisibilityRule,
  type GatedColumn,
  type GatedColumnTable,
  type MediaTypeKey,
  type ProfileFieldKey,
  type ViewContext,
  type ViewerRole,
  type ViewKey,
} from './lib/view-fields';

// Directory-level visibility SCOPES — the coarse adapter dancer-listing
// surfaces speak (fever_staff | ballee_admin | dancer_self | dancer_peer |
// public). Derives every field decision from the view-fields registry; owns no
// parallel field list. Server loaders compute the scope and run rows through
// `dancerFieldsForScope` before returning. See `lib/visibility-scopes.ts`.
export {
  dancerFieldsForScope,
  scopeAllowsCastSignals,
  scopeToViewContext,
  type VisibilityScope,
} from './lib/visibility-scopes';
